import { userSession, endSession } from 'state/session';
import { tenantConfig } from 'utils/tenant';
import { user } from 'utils/auth/user';
import logger from 'utils/log';

/**
 * Activity events that should reset the timeout timer
 */
const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'touchmove',
  'click',
  'focus',
] as const;

/**
 * Throttle delay for activity detection (in ms)
 * This prevents excessive timer resets from rapid events like mousemove
 */
const ACTIVITY_THROTTLE_DELAY = 1000;

interface ITimeoutAgent {
  start(): void;
  stop(): void;
  reset(): void;
  isActive(): boolean;
  getRemainingTime(): number;
  showWarning(): void;
  hideWarning(): void;
}

/**
 * Session timeout management class
 * Monitors user activity and automatically ends inactive sessions
 */
class SessionTimeoutAgent implements ITimeoutAgent {
  private timeoutId: NodeJS.Timeout | null = null;
  private warningTimeoutId: NodeJS.Timeout | null = null;
  private lastActivityTime = Date.now();
  private isRunning = false;
  private warningShown = false;
  private activityThrottle = false;

  constructor() {
    this.handleActivity = this.handleActivity.bind(this);
    this.handleTimeout = this.handleTimeout.bind(this);
    this.showTimeoutWarning = this.showTimeoutWarning.bind(this);
  }

  /**
   * Start the timeout agent
   * Only dealers can start timeout monitoring as only they can end sessions
   */
  start(): void {
    if (!user.isDealer) {
      logger.debug('Timeout agent not started - user is not a dealer');
      return;
    }

    if (!tenantConfig().isAllowed('sessionTimeout')) {
      logger.debug('Timeout agent not started - timeout is disabled in tenant config');
      return;
    }

    if (this.isRunning) {
      logger.debug('Timeout agent already running');
      return;
    }

    this.isRunning = true;
    this.lastActivityTime = Date.now();
    this.warningShown = false;

    this.addActivityListeners();

    // Start the timeout timer
    this.resetTimer();

    const sessionTimeout = tenantConfig().config?.sessionTimeout;
    logger.info('[Timeout] Session timeout agent started', {
      timeoutMinutes: sessionTimeout?.timeoutMinutes,
      warningMinutes: sessionTimeout?.warningMinutes,
    });
  }

  /**
   * Stop the timeout agent and cleanup
   */
  stop(): void {
    if (!this.isRunning) return;

    this.isRunning = false;

    // Clear timers
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId);
      this.warningTimeoutId = null;
    }

    // Remove activity listeners
    this.removeActivityListeners();

    // Hide any visible warnings
    this.hideWarning();

    logger.info('[Timeout] Session timeout agent stopped');
  }

  /**
   * Reset the timeout timer (called when user activity is detected)
   */
  reset(): void {
    if (!this.isRunning) return;

    this.lastActivityTime = Date.now();

    // Hide warning if it was shown
    if (this.warningShown) {
      this.hideWarning();
    }

    this.resetTimer();
  }

  /**
   * Check if the timeout agent is currently active
   */
  isActive(): boolean {
    return this.isRunning;
  }

  /**
   * Get remaining time before timeout (in milliseconds)
   */
  getRemainingTime(): number {
    if (!this.isRunning) return 0;

    const config = tenantConfig().config;
    if (!config?.sessionTimeout) return 0;

    const timeoutDuration = config.sessionTimeout.timeoutMinutes * 60 * 1000;
    const elapsed = Date.now() - this.lastActivityTime;
    return Math.max(0, timeoutDuration - elapsed);
  }

  /**
   * Show timeout warning to the user
   */
  showWarning(): void {
    if (this.warningShown) return;

    this.warningShown = true;

    // Emit a warning event that UI components can listen to
    userSession().emit('session:timeout-warning', {
      remainingMinutes: Math.ceil(this.getRemainingTime() / (60 * 1000)),
    });

    logger.warn('[Timeout] Session timeout warning shown', {
      remainingTime: this.getRemainingTime(),
    });
  }

  /**
   * Hide timeout warning
   */
  hideWarning(): void {
    if (!this.warningShown) return;

    this.warningShown = false;

    // Emit event to hide warning
    userSession().emit('session:timeout-warning-dismissed');

    logger.debug('[Timeout] Session timeout warning dismissed');
  }

  /**
   * Add activity event listeners to the document
   */
  private addActivityListeners(): void {
    ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, this.handleActivity, {
        passive: true,
        capture: true,
      });
    });
  }

  /**
   * Remove activity event listeners
   */
  private removeActivityListeners(): void {
    ACTIVITY_EVENTS.forEach((event) => {
      document.removeEventListener(event, this.handleActivity, true);
    });
  }

  /**
   * Handle user activity events (throttled)
   */
  private handleActivity(): void {
    if (!this.isRunning || this.activityThrottle) return;

    // Throttle activity handling to prevent excessive timer resets
    this.activityThrottle = true;
    setTimeout(() => {
      this.activityThrottle = false;
    }, ACTIVITY_THROTTLE_DELAY);

    this.reset();
  }

  /**
   * Reset the timeout and warning timers
   */
  private resetTimer(): void {
    const config = tenantConfig().config;
    if (!config?.sessionTimeout) return;

    // Clear existing timers
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId);
      this.warningTimeoutId = null;
    }

    const timeoutMs = config.sessionTimeout.timeoutMinutes * 60 * 1000;
    const warningMs = config.sessionTimeout.warningMinutes * 60 * 1000;
    const warningDelay = timeoutMs - warningMs;

    // Set warning timer (show warning before timeout)
    this.warningTimeoutId = setTimeout(this.showTimeoutWarning, warningDelay);

    // Set timeout timer (end session)
    this.timeoutId = setTimeout(this.handleTimeout, timeoutMs);
  }

  /**
   * Show timeout warning
   */
  private showTimeoutWarning(): void {
    if (!this.isRunning) return;
    this.showWarning();
  }

  /**
   * Handle session timeout
   */
  private async handleTimeout(): Promise<void> {
    if (!this.isRunning) return;

    logger.warn('[Timeout] Session timed out due to inactivity');

    // Stop the agent to prevent multiple timeouts
    this.stop();

    try {
      // End the session with timeout flag
      await endSession({
        isTimeout: true,
        reason: 'inactivity-timeout',
        keepAlive: true,
      });
    } catch (error) {
      logger.error('[Timeout] Failed to end session on timeout', error);
    }
  }
}

// Global timeout agent instance
let timeoutAgent: SessionTimeoutAgent | null = null;

/**
 * Initialize the session timeout agent
 * This should be called when a session starts
 */
export function initSessionTimeout(): SessionTimeoutAgent {
  if (!timeoutAgent) {
    timeoutAgent = new SessionTimeoutAgent();
  }
  return timeoutAgent;
}

/**
 * Get the current timeout agent instance
 */
export function getTimeoutAgent(): SessionTimeoutAgent | null {
  return timeoutAgent;
}

/**
 * Start monitoring session timeout
 * Only starts if dealer and timeout is enabled in tenant config
 */
export function startSessionTimeout(): void {
  const agent = initSessionTimeout();
  agent.start();
}

/**
 * Stop monitoring session timeout
 */
export function stopSessionTimeout(): void {
  if (timeoutAgent) {
    timeoutAgent.stop();
  }
}

/**
 * Reset the session timeout timer (call when user activity is detected)
 */
export function resetSessionTimeout(): void {
  if (timeoutAgent) {
    timeoutAgent.reset();
  }
}

/**
 * Check if session timeout is currently active
 */
export function isSessionTimeoutActive(): boolean {
  return timeoutAgent?.isActive() ?? false;
}

/**
 * Get remaining time before session timeout (in milliseconds)
 */
export function getRemainingTimeoutTime(): number {
  return timeoutAgent?.getRemainingTime() ?? 0;
}
